import { takeEvery, call, put, all, fork, select, take, delay } from 'typed-redux-saga';
import { get, isString } from "lodash-es";

import * as store from './reducers';
import ConfigurationClient from '../web-apis/configuration-client';
import { setModelCustomizations, setFoundTextEditorFilePaths, setTextEditorFileContents, updateLicenseStart, importModelCustomizationFailure, setModelCustomizationObjectsAsModified } from './reducers';
import TextEditorFilesApi from '../web-apis/text-editor-files-api';
import { TextEditorFile } from './text-editor-file';
import * as selectors from './selectors';
import { Action, Dispatch } from '@reduxjs/toolkit';
import { Labeling } from './labeling';
import { createRequiredAppAuthState } from './auth';
import { POPUPS_BLOCKED_ERROR, POPUP_WINDOW_ERROR } from '../web-apis/app-auth';
import { createLoginError } from './store-errors';
import { backendTierAppAuths } from '../web-apis/cloud-backend-auth';
import { MVisionAppClient, UNAUTHENTICATED_APP } from './mvision-client-list';
import { LicenseStatus } from './license-status';
import { ModelCustomizationEntities, ModelCustomizationEntitiesForExport, ModelCustomizationOutputEntities } from './model-customization-types';
import { isInstanceOfError } from '../util/errors';
import { WebConfigurationClient } from '../web-apis/web-configuration';
import { daemonPrefix, generateNewSessionId } from '../environments';
import { getConfigClient, LoginType } from '../app-config';
import { DaemonConfig } from './daemon-config';
import { downloadFileToDisk } from '../util/files';
import { convertModelCustomizationsToJson } from './model-customization-helpers';
import UserSettings from './user-settings';
import { AccessRights, getInitialUserAccessRights, getBypassedAccessRights } from './access-rights';
import { DeploymentMode, DisabledFeature, isFeatureDisabled } from './deployment-config-info';
import { HelpDocument } from '../i18n';


function* fetchModelCustomizationsSaga(appClient: MVisionAppClient | undefined) {
    const client = new ConfigurationClient();
    let modelCustomizations: ModelCustomizationEntities | null = null;
    let error = null;

    try {
        modelCustomizations = appClient === undefined ? null : yield* call(async () => client.fetchModelCustomizationsAsync(appClient));
        if (modelCustomizations !== null) {
            const fetchModels = yield* call(async () => client.fetchAvailableModelsAsync(appClient));
            if (fetchModels !== undefined) {
                // if the available models collection is in use, mark which segmentation models are unavailable
                for (const model of modelCustomizations.models) {
                    if (fetchModels.deprecatedModels.includes(model.modelName)) {
                        model.isDeprecated = true;
                    }
                    model.isAvailable = fetchModels.availableModels.includes(model.modelName);
                }
            }
        }
    }
    catch (ex) {
        console.error(ex);
        error = ex;
    }

    if (isInstanceOfError(error)) {
        yield* put({ type: setModelCustomizations, payload: { customizations: null, error: error.message || 'Unspecified error.' } });
        return false;
    } else {
        yield* put({ type: setModelCustomizations, payload: { customizations: modelCustomizations } });
        return true;
    }
}

function* saveModelCustomizationSaga(action: any) {
    if (store.saveModelCustomization.match(action)) {
        const { appClient } = action.payload;
        const client = new ConfigurationClient();
        let error = null;
        let errorMessage = null;
        let success = false;

        try {
            // collect model customization -- this is essentially a cache database dump (but global rois are not needed)
            const modelCustomization: ModelCustomizationEntitiesForExport = yield* collectModelCustomizationForExport();

            // sanity-check that everything is valid in case invalid data slips through the UI debouncing
            if (modelCustomization.aeTitleRules.some(r => !r.isValid)
                || modelCustomization.dicomRules.some(r => !r.isValid)
                || modelCustomization.dicomAttributeRules.some(r => !r.isValid)) {
                throw new Error(`Model configuration has invalid entries. Please fix them before saving.`);
            }

            yield* put(store.saveModelCustomizationStart());
            success = yield* call(async () => client.saveModelCustomizationAsync(appClient, modelCustomization));
        }
        catch (ex) {
            console.error(ex);
            error = ex;
        }

        if (error !== null) {
            console.error('Was not able to save model customization');
            errorMessage = `${get(error, 'problem', 'Error')}: ${get(error, 'message', 'Unknown error')}`;
            console.error(errorMessage);
        } else if (!success) {
            errorMessage = 'Unable to save model customization. There might be a problem with the server.';
        }

        yield* put(store.saveModelCustomizationFinish(success, errorMessage || undefined, isInstanceOfError(error) ? error : undefined));
    }
}

function* resetAllModelCustomizationsSaga(action: any) {
    if (store.resetAllModelCustomizations.match(action)) {
        const { appClient } = action.payload;
        const client = new ConfigurationClient();
        let error = null;
        let errorMessage = null;
        let success = false;

        try {
            yield* put(store.resetModelCustomizationStart());
            success = yield* call(async () => client.deleteModelCustomizationAsync(appClient));
            if (success) {
                // immediately fetch updated (reset) customizations
                success = yield* call(fetchModelCustomizationsSaga, appClient);
            }
        }
        catch (ex) {
            console.error(ex);
            error = ex;
        }

        if (error !== null) {
            console.error('Was not able to reset model customizations');
            errorMessage = `${get(error, 'problem', 'Error')}: ${get(error, 'message', 'Unknown error')}`;
            console.error(errorMessage);
        } else if (!success) {
            errorMessage = 'Unable to reset model customizations. There might be a problem with the server.';
        }

        yield* put(store.resetModelCustomizationFinish(success, errorMessage || undefined, isInstanceOfError(error) ? error : undefined));
    }
}

function* resetSingleModelCustomizationSaga(action: Action) {
    if (store.resetSingleModelCustomization.match(action)) {

        const { customizationOutputId } = action.payload;

        const output = yield* select(selectors.selectCustomizationOutputById, customizationOutputId);
        const modelName = yield* select(selectors.getModelNameForCustomizationOutput, customizationOutputId);

        if (!output || !modelName) {
            throw new Error('No model name specified, cannot fetch default configuration for model');
        }

        const client = new ConfigurationClient();
        let error = null;
        let errorMessage = null;
        let modelCustomizationEntities: ModelCustomizationOutputEntities | null = null;


        try {
            yield* put(store.resetSingleCustomizationOutputStart());

            // get default config for this model
            modelCustomizationEntities = yield* call(async () => client.fetchDefaultConfigurationForCustomizationOutput(modelName));

            // replace existing output and its items with received default items
            yield* put(store.replaceModelCustomizationOutput(customizationOutputId, modelCustomizationEntities));
        }
        catch (ex) {
            console.error(ex);
            error = ex;
        }

        if (error !== null) {
            console.error(`Was not able to reset single customization output (${customizationOutputId})`);
            errorMessage = `An error occurred when trying to reset target file: ${get(error, 'problem', 'Error')}: ${get(error, 'message', 'Unknown error')}`;
            console.error(errorMessage);
        }

        const success = error === null;
        yield* put(store.resetSingleCustomizationOutputFinish(success, errorMessage || undefined, isInstanceOfError(error) ? error : undefined));
    }
}


function* fetchTextEditorFilesSaga() {
    let files: TextEditorFile[] = [];
    let error = null;

    try {
        files = yield* call(TextEditorFilesApi.searchFiles);
    }
    catch (ex) {
        console.error(ex);
        error = ex;
    }

    if (error === null) {
        yield* put({ type: setFoundTextEditorFilePaths, payload: { foundFiles: files } });
        for (let file of files) {
            // after putting text editor files to store the file objects we have here may already
            // be stale, so immediately re-fetch latest version of this file from the store so we
            // don't have to duplicate store logic here
            const latestFile = yield* select(selectors.getLatestTextEditorFile, file);
            yield* fork(tryFetchTextEditorFileContentsSaga, { payload: { file: latestFile } });
        }
    }
}

function* tryFetchTextEditorFileContentsSaga(action: any) {
    const file: TextEditorFile = get(action, 'payload.file', null);
    if (file !== null) {
        // allow file to load (or re-load) if it's not already loading, or doesn't have modifications
        if (!file.isLoadingFile && !file.isModified) {
            yield* put({ type: store.fetchTextEditorFileContents, payload: { filePath: file.filePath } });

            let contents: string = '';
            let error = null;

            try {
                contents = yield* call(TextEditorFilesApi.loadFile, file.filePath);
            }
            catch (ex) {
                console.error(ex);
                error = ex;
            }

            if (error === null) {
                // TODO: show errors in UI
                yield* put({ type: setTextEditorFileContents, payload: { filePath: file.filePath, contents: contents } });
            }
        }
    }
}

function* saveTextEditorFileContentsSaga(action: any) {
    const { filePath, newContents } = action.payload;

    let error = null;

    try {
        yield* call(TextEditorFilesApi.saveFile, filePath, newContents);
    }
    catch (ex) {
        console.error(ex);
        error = ex;
    }

    if (error === null) {
        yield* put({ type: store.saveTextEditorFileContentsFinish, payload: { filePath: filePath, newContents: newContents } });
    } else {
        // error case
        console.error(`Was not able to save changes to file ${filePath}`);
        const errorMessage = get(error, 'message', 'Unknown error');
        yield* put({ type: store.saveTextEditorFileContentsFinish, payload: { filePath: filePath, newContents: null, error: errorMessage } });
    }
}

function* fetchLicenseStatusSaga(appClient: MVisionAppClient | undefined) {
    const client = new ConfigurationClient();
    let licenseStatus: LicenseStatus[] | null = null;
    let error = null;

    try {
        licenseStatus = appClient === undefined ? [] : yield* call(async () => client.fetchLicenseStatusAsync(appClient));
    }
    catch (ex) {
        console.error(ex);
        error = ex;
    }

    if (error === null) {
        yield* put({ type: store.setLicenseStatus, payload: { licenseStatus: licenseStatus } });
    } else {
        // error case
        console.error('Was not able to fetch license status');
        const errorMessage = `${get(error, 'problem', 'Error')}: ${get(error, 'message', 'Unknown error')}`;
        yield* put({ type: store.setLicenseStatus, payload: { licenseStatus: null, error: errorMessage } });
    }
}


function* updateLicenseSaga(action: Action) {
    if (updateLicenseStart.match(action)) {
        const { newLicense, appClient } = action.payload;
        const client = new ConfigurationClient();
        let error = null;
        let errorMessage = null;
        let success = false;

        try {
            success = yield* call(async () => client.saveLicenseAsync(appClient, newLicense));
        }
        catch (ex) {
            console.error(ex);
            error = ex;
        }

        if (error !== null) {
            console.error('Was not able to update license');
            errorMessage = `${get(error, 'problem', 'Error')}: ${get(error, 'message', 'Unknown error')}`;
            console.error(errorMessage);
        } else if (!success) {
            errorMessage = 'Unable to update license. The given license key might not be valid, or there might be a problem with the server. Please check Cockpit logs.';
        }

        yield* put({ type: store.updateLicenseFinish, payload: { success: success, error: errorMessage } });

        if (success) {
            // immediately fetch the new license
            yield* fork(fetchLicenseStatusSaga, appClient);
        }
    }

}

function* fetchDaemonConfigsSaga(appClient: MVisionAppClient | undefined) {
    const client = new ConfigurationClient();
    let daemonConfigs: DaemonConfig[] | null = null;
    let error = null;

    try {
        daemonConfigs = appClient === undefined ? null : yield* call(async () => client.fetchDaemonConfigsAsync(appClient));
    }
    catch (ex) {
        console.error(ex);
        error = ex;
    }

    if (error === null) {
        yield* put({ type: store.setDaemonConfigs, payload: { daemonConfigs: daemonConfigs } });
    } else {
        // error case
        console.error('Was not able to fetch daemon configuration');
        const errorMessage = `${get(error, 'problem', 'Error')}: ${get(error, 'message', 'Unknown error')}`;
        yield* put({ type: store.setDaemonConfigs, payload: { daemonConfigs: null, error: errorMessage } });
    }
}

/** Fetches a single daemon configuration, not all of them. Generally you'll want to use **fetchDaemonConfigsSaga** to get all of them instead of just one. */
function* fetchSingleDaemonConfigSaga(appClient: MVisionAppClient | undefined, daemonClientId: string) {
    const client = new ConfigurationClient();
    let daemonConfig: DaemonConfig | null = null;
    let error = null;

    try {
        daemonConfig = appClient === undefined ? null : yield* call(async () => client.fetchDaemonConfigAsync(appClient, daemonClientId));
    }
    catch (ex) {
        console.error(ex);
        error = ex;
    }

    if (isInstanceOfError(error)) {
        yield* put(store.setDaemonConfig(daemonConfig, error.message || 'Unspecified error.'));
        return false;
    } else {
        yield* put(store.setDaemonConfig(daemonConfig));
        return true;
    }
}

function* saveDaemonConfigSaga(appClient: MVisionAppClient | undefined, daemonConfig: DaemonConfig) {
    const client = new ConfigurationClient();
    let error = null;
    let errorMessage = null;
    let success = false;

    try {
        yield* put(store.saveDaemonConfigStart(appClient, daemonConfig));
        success = yield* call(async () => client.saveDaemonConfigAsync(appClient, daemonConfig));
    }
    catch (ex) {
        console.error(ex);
        error = ex;
    }

    if (error !== null) {
        console.error('Was not able to save daemon configuration');
        errorMessage = `${get(error, 'problem', 'Error')}: ${get(error, 'message', 'Unknown error')}`;
        console.error(errorMessage);
    } else if (!success) {
        errorMessage = 'Unable to save daemon configuration. There might be a problem with the server.';
    }

    yield* put(store.saveDaemonConfigFinish(appClient, daemonConfig, success, errorMessage || undefined, isInstanceOfError(error) ? error : undefined));
}

function* resetDaemonConfigSaga(appClient: MVisionAppClient | undefined, daemonConfig: DaemonConfig) {
    const client = new ConfigurationClient();
    let error = null;
    let errorMessage = null;
    let success = false;

    try {
        yield* put(store.resetDaemonConfigStart(appClient, daemonConfig));
        success = yield* call(async () => client.resetDaemonConfigAsync(appClient, daemonConfig));
        if (success) {
            // wait a bit, then fetch updated (reset) daemon config
            yield* delay(2000);  // 2 seconds
            success = yield* call(fetchSingleDaemonConfigSaga, appClient, daemonConfig.sessionId);
        }
    }
    catch (ex) {
        console.error(ex);
        error = ex;
    }

    if (error !== null) {
        console.error('Was not able to reset daemon configuration');
        errorMessage = `${get(error, 'problem', 'Error')}: ${get(error, 'message', 'Unknown error')}`;
        console.error(errorMessage);
    } else if (!success) {
        errorMessage = 'Unable to reset daemon configuration. There might be a problem with the server.';
    }

    yield* put(store.resetDaemonConfigFinish(appClient, daemonConfig, success, errorMessage || undefined, isInstanceOfError(error) ? error : undefined));
}

function* removeDaemonConfigSaga(appClient: MVisionAppClient | undefined, daemonConfig: DaemonConfig) {
    const client = new ConfigurationClient();
    let error = null;
    let errorMessage = null;
    let success = false;

    try {
        yield* put(store.removeDaemonConfigStart(appClient, daemonConfig));
        success = yield* call(async () => client.deleteDaemonConfigAsync(appClient, daemonConfig));
        if (success) {
            // remove the just-removed entry from store
            yield* put(store.unsetDaemonConfig(daemonConfig.sessionId));
        }
    }
    catch (ex) {
        console.error(ex);
        error = ex;
    }

    if (error !== null) {
        console.error('Was not able to remove daemon configuration');
        errorMessage = `${get(error, 'problem', 'Error')}: ${get(error, 'message', 'Unknown error')}`;
        console.error(errorMessage);
    } else if (!success) {
        errorMessage = 'Unable to remove daemon configuration. There might be a problem with the server.';
    }

    yield* put(store.removeDaemonConfigFinish(appClient, daemonConfig, success, errorMessage || undefined, isInstanceOfError(error) ? error : undefined));
}

function* fetchLabelingSaga() {
    const client = new ConfigurationClient();
    let productLabel: Labeling | null = null;
    let error = null;

    try {
        productLabel = yield* call(async () => client.fetchLabelingAsync());
    }
    catch (ex) {
        console.error(ex);
        error = ex;
    }

    if (error === null) {
        yield* put({ type: store.setLabeling, payload: { productLabel: productLabel } });
    } else {
        // error case
        console.error('Was not able to fetch product label');
        const errorMessage = `${get(error, 'problem', 'Error')}: ${get(error, 'message', 'Unknown error')}`;
        yield* put({ type: store.setLabeling, payload: { productLabel: null, error: errorMessage } });
    }
}

function* collectModelCustomizationForExport() {
    const modelCustomization: ModelCustomizationEntitiesForExport = {
        models: yield* select(selectors.selectModels),
        customizationBases: yield* select(selectors.selectCustomizationBases),
        customizationOutputs: yield* select(selectors.selectCustomizationOutputs),
        roiCustomizations: yield* select(selectors.selectRoiCustomizations),
        modelCustomizationsMetadata: yield* select(selectors.selectModelCustomizationMetadata),
        aeTitleRules: yield* select(selectors.selectAeTitleRules),
        dicomRules: yield* select(selectors.selectDicomRules),
        dicomAttributeRules: yield* select(selectors.selectDicomAttributeRules),
    };

    return modelCustomization;
}

function* exportModelCustomization(appClient: MVisionAppClient | undefined) {
    try {
        // collect model customization -- this is essentially a cache database dump (but global rois are not needed)
        const modelCustomization: ModelCustomizationEntitiesForExport = yield* collectModelCustomizationForExport();
        const jsonObject = yield* call(async() => convertModelCustomizationsToJson(modelCustomization, true));

        // Default filename if appClient?.userName is undefined or empty
        const defaultFilename = "mvision-model-customizations.json";
        const localModeFilename = "local.json";
        const appDeploymentInfo = yield* select(selectors.getAppDeploymentInfo);
        const isLocalMode = appDeploymentInfo && appDeploymentInfo.deploymentMode === DeploymentMode.Local;
        const filename = isLocalMode ? localModeFilename : (appClient?.userName ? `${appClient.userName}.json` : defaultFilename);


        downloadFileToDisk(JSON.stringify(jsonObject, null, 2), filename, 'application/json');

    } catch (error) {
        yield* put({ type: store.exportModelCustomization, payload: { error: error } });
    }
}

function* importModelCustomization(modelCustomization: ModelCustomizationEntities) {
    try {
        yield* put(setModelCustomizations(modelCustomization));
        yield* put(setModelCustomizationObjectsAsModified(modelCustomization)); // set the entire ModelCustomizationEntities object as modified
        yield* put({ type: importModelCustomizationFailure, payload: { errorMessage: null } });
    } catch (errorMessage) {
        yield* put({ type: importModelCustomizationFailure, payload: { errorMessage: errorMessage } });
    }
}

function* fetchStatisticsSaga(appClient: MVisionAppClient | undefined) {
    // const client = new ConfigurationClient();
    // // let productLabel: Labeling | null = null;
    // let error = null;

    // try {
    //     const statistics = yield* call(async () => client.fetchStatisticsAsync(appClient));
    // }
    // catch (ex) {
    //     console.error(ex);
    //     error = ex;
    // }

    // if (error === null) {
    //     yield* put({ type: store.setLabeling, payload: { productLabel: productLabel } });
    // } else {
    //     // error case
    //     console.error('Was not able to fetch product label');
    //     const errorMessage = `${get(error, 'problem', 'Error')}: ${get(error, 'message', 'Unknown error')}`;
    //     yield* put({ type: store.setLabeling, payload: { productLabel: null, error: errorMessage } });
    // }
}

function* fetchClientListSaga() {

    const client = new ConfigurationClient();
    let clientList: MVisionAppClient[] | undefined | null = null;
    let error = null;


    try {
        clientList = yield* call(async () => client.fetchClientListAsync());
    }
    catch (ex) {
        console.error(ex);
        error = ex;
    }

    if (error === null) {
        yield* put({ type: store.setClientList, payload: { clientList: clientList } });

        // immediately set current app client in certain conditions
        // (re-select client list here in case we do any post-processing
        // for the list in the reducer in future)
        clientList = yield* select(selectors.selectClientList);
        if (clientList !== undefined) {
            if (clientList.length === 1) {
                // if we only have one item available, auto-select it
                yield* put(store.setCurrentAppClient(clientList[0].userId));
            } else {
                // if there's exactly one daemon client available, auto-select it
                const daemons = clientList.filter(c => c.userName.startsWith(daemonPrefix));
                if (daemons.length === 1) {
                    yield* put(store.setCurrentAppClient(daemons[0].userId));
                }
            }
        }
    } else {
        // error case
        console.error('Was not able to fetch client list');
        // TODO: this format might be cockpit-specific? In that case it must be fixed everywhere in this file.
        const errorMessage = isString(error) ? error : `${get(error, 'problem', 'Error')}: ${get(error, 'message', 'Unknown error')}`;
        yield* put({ type: store.setClientList, payload: { clientList: null, error: `An error occurred when trying to retrieve client list: ${errorMessage}` } });
    }
}

// Saga function to fetch access rights
function* fetchAccessRightsSaga() {
    const client = new ConfigurationClient();
    let accessRights: AccessRights | null = null;
    let error = null;

    try {
        // Call the API to fetch access rights
        // bypass this if we're in local mode or the feature has been disabled in deployment config
        const config = yield* select(selectors.getAppDeploymentInfo);
        if (!config) { throw new Error('No deployment config file found'); }
        if (config.deploymentMode === DeploymentMode.Local || isFeatureDisabled(config, DisabledFeature.AccessRights)) {
            accessRights = getBypassedAccessRights();
        } else {
            accessRights = yield* call(() => client.fetchAndParseAccessRights());
        }
    } catch (err) {
        // Handle any errors during API call
        console.error(err);
        error = err;
        yield* put({ type: store.setAccessRights, payload: { accessRights: getInitialUserAccessRights(), error: 'An error occurred when trying to retrieve access rights' } });
    }

    if (!error) {
        // If no error occurred during API call
        yield* put({ type: store.setAccessRights, payload: { accessRights }});
    }
}

function* fetchUserSettingsSaga() {
    const client = new ConfigurationClient();
    let userSettings: UserSettings | null = null;
    let error = null;

    try {
        userSettings = yield* call(async () => client.fetchUserSettingsAsync());
    }
    catch (ex) {
        console.error(ex);
        error = ex;
    }

    if (error === null && userSettings !== null) {
        yield* put({ type: store.setUserSettings, payload: { userSettings: userSettings } });
    } else {
        // error case
        console.error('Was not able to fetch user settings');
        const errorMessage = `${get(error, 'problem', 'Error')}: ${get(error, 'message', 'Unknown error')}`;
        yield* put({ type: store.setUserSettings, payload: { userSettings: null, error: errorMessage } });
    }
}


function* firstTimeCloudLoginSaga() {

    // return out of this function immediately if azure login is not warranted
    const appConfig = yield* select(selectors.getAppConfig);
    if (!appConfig) { throw new Error('App config not initialized -- cannot log in'); }
    if (appConfig.loginType !== LoginType.AzureLogin) {
        return;
    }

    const backend = yield* select(selectors.getCloudBackend);
    if (backend === undefined) {
        throw new Error('No backend defined');
    }

    // create a new app auth state for tracking user's login process
    const appAuth = backendTierAppAuths[backend.tier];
    const appAuthState = createRequiredAppAuthState(appAuth.appName);
    yield* put(store.addAppAuthState(appAuthState));

    // log in
    yield* put(store.startAppAuthLogin(appAuthState.appAuthName));
    try {
        yield* call([appAuth, 'logIn']);

        yield* put(store.logInSuccess(appAuthState.appAuthName));

        // do some extra stuff after login
        // immediately fetch access rights, user settings & client list
        yield* put(store.fetchAccessRights());
        // yield* put(store.fetchUserSettings()); // TODO: fetch user settings here once we have something to actually fetch and use!
        yield* put(store.fetchClientList());

        // // first authentication should also have authenticated current user with rtviewer backend
        // const userDetails: { username: string, email: string, permissions: UserPermissions } = yield* call([rtViewerApiClient, 'getAuthenticatedUser']);
        // yield* put({ type: Store.setUserDetails, ...userDetails });
    } catch (err) {
        const loginError = isInstanceOfError(err) ?
            createLoginError(('message' in err && (err.message === POPUPS_BLOCKED_ERROR || err.message.includes(POPUP_WINDOW_ERROR))), err.message)
            : createLoginError(false, get(err, 'message', 'An error occurred'));
        yield* put(store.setLoginError(loginError));
    }
}



function* initializeAppSaga(dispatch: Dispatch) {
    // generate a client ID and load version & configuration JSON files
    yield* all([call(generateNewSessionId), call(getAppVersion), call(getAppDeploymentInfo)]);

    // set correct app config
    const deploymentInfo = yield* select(selectors.getAppDeploymentInfo);
    if (!deploymentInfo) {
        throw new Error('App initialization failed -- could not retrieve app deployment info');
    }
    const configClient = getConfigClient(deploymentInfo.deploymentMode);
    yield* put(store.setAppConfig(configClient));

    // initialise current backend client configuration
    yield* call(async () => configClient.getBackendApi().init(dispatch, deploymentInfo));

    // app initialization is done
    yield* put(store.appInitializeSuccess());

    // start possible first-time auth
    yield* fork(firstTimeCloudLoginSaga);

    // start watching updated version.json
    yield* fork(watchAppVersionSaga);

    // these initializations can be done concurrently with other things
    yield* all([call(getHelpDocuments)]);
}

function* getAppVersion() {
    try {
        const appVersion = yield* call(WebConfigurationClient.getAppVersionInfo);
        yield* put(store.setAppVersion(appVersion));
    }
    catch (error) {
        console.log('An error occurred when trying to retrieve app version:')
        console.error(error);
        yield* put(store.appInitializeFailure((error as Error).toString()));
    }
}

function* getAppDeploymentInfo() {
    try {
        const appDeploymentInfo = yield* call(WebConfigurationClient.getDeploymentConfigInfo);
        yield* put(store.setAppDeploymentInfo(appDeploymentInfo));
    }
    catch (error) {
        console.log('An error occurred when trying to retrieve app config:')
        console.error(error);
        yield* put(store.appInitializeFailure((error as Error).toString()));
    }
}

function* getHelpDocuments() {
    let helpDocuments: HelpDocument[];
    try {
        helpDocuments = yield* call(WebConfigurationClient.getHelpDocuments);
    }
    catch (error) {
        // just don't show any help documents if something went wrong
        helpDocuments = [];
    }

    yield* put(store.setHelpDocuments(helpDocuments));
}

function* watchAppVersionSaga() {
    // start a saga for watching when user snoozes the version alert dialog
    yield* fork(watchVersionAlertSnoozeSaga);
    // wait for 1 hour before doing another version check
    const waitTimeBetweenVersionChecksInMs = (1 * 60 * 60 * 1000);
    while (true) {
        yield* delay(waitTimeBetweenVersionChecksInMs);
        yield* call(getAppVersion);
    }
}

function* watchVersionAlertSnoozeSaga() {
    while (true) {
        const action = yield* take(store.snoozeNewVersionAlertFor);
        const { snoozeForMs } = action.payload;
        yield* put(store.setNewVersionAlertSnooze(true));
        yield* delay(snoozeForMs)
        yield* put(store.setNewVersionAlertSnooze(false));
    }
}

function* watchUserLogOutSaga() {
    while (true) {
        yield* take(store.requestLogOut);
        const firstLoggedInAppAuth = Object.values(backendTierAppAuths).find(a => a.isLoggedIn);
        if (firstLoggedInAppAuth) {
            yield* call(async () => firstLoggedInAppAuth.logOut());
        }
    }
}

function* watchSaveModelCustomizationSaga() {
    // take all model customization save requests as they come -- do limiting simultaenous saves by temporarily 
    // disabling relevant ui elements instead of doing it with sagas here
    yield* takeEvery(store.saveModelCustomization, saveModelCustomizationSaga);
}

function* watchResetAllModelCustomizationsSaga() {
    yield* takeEvery(store.resetAllModelCustomizations, resetAllModelCustomizationsSaga);
}

function* watchResetSingleModelCustomizationSaga() {
    yield* takeEvery(store.resetSingleModelCustomization, resetSingleModelCustomizationSaga);
}


function* watchFetchTextEditorFilesSaga() {
    yield* takeEvery(store.fetchTextEditorFiles, fetchTextEditorFilesSaga);
}

function* watchFetchTextEditorFileContentsSaga() {
    yield* takeEvery(store.tryFetchTextEditorFileContents, tryFetchTextEditorFileContentsSaga);
}

function* watchSaveTextEditorFileContentsSaga() {
    yield* takeEvery(store.saveTextEditorFileContentsStart, saveTextEditorFileContentsSaga);
}

function* watchFetchLicenseStatusSaga() {
    while (true) {
        const action = yield* take(store.fetchLicenseStatus);
        if (store.fetchLicenseStatus.match(action)) {
            yield* fork(fetchLicenseStatusSaga, action.payload.appClient);
        }
    }
}

function* watchFetchDaemonConfigsSaga() {
    while (true) {
        const action = yield* take(store.fetchDaemonConfigs);
        if (store.fetchDaemonConfigs.match(action)) {
            yield* fork(fetchDaemonConfigsSaga, action.payload.appClient);
        }
    }
}

function* watchSaveDaemonConfigSaga() {
    while (true) {
        const action = yield* take(store.saveDaemonConfig);
        if (store.saveDaemonConfig.match(action)) {
            yield* fork(saveDaemonConfigSaga, action.payload.appClient, action.payload.daemonConfig);
        }
    }
}

function* watchResetDaemonConfigSaga() {
    while (true) {
        const action = yield* take(store.resetDaemonConfig);
        if (store.resetDaemonConfig.match(action)) {
            yield* fork(resetDaemonConfigSaga, action.payload.appClient, action.payload.daemonConfig);
        }
    }
}

function* watchRemoveDaemonConfigSaga() {
    while (true) {
        const action = yield* take(store.removeDaemonConfig);
        if (store.removeDaemonConfig.match(action)) {
            yield* fork(removeDaemonConfigSaga, action.payload.appClient, action.payload.daemonConfig);
        }
    }
}

function* watchExportModelCustomizationSaga() {
    while (true) {
        const action = yield* take(store.exportModelCustomization);
        if (store.exportModelCustomization.match(action)) {
            yield* fork(exportModelCustomization, action.payload.appClient);
        }
    }
}

function* watchImportModelCustomizationSaga() {
    while (true) {
        const action = yield* take(store.importModelCustomization);
        const errorMessage = 'No JSON data provided for import.';

        if (!action.payload.json) {
            yield* put({ type: importModelCustomizationFailure, payload: { errorMessage } });
            continue;
        }

        yield* fork(importModelCustomization, action.payload.json);
    }
}


function* watchUpdateLicenseSaga() {
    yield* takeEvery(store.updateLicenseStart, updateLicenseSaga);
}

function* watchFetchLabelingSaga() {
    yield* takeEvery(store.fetchLabeling, fetchLabelingSaga);
}

function* watchFetchStatisticsSaga() {
    while (true) {
        const action = yield* take(store.fetchStatistics);
        if (store.fetchStatistics.match(action)) {
            yield* fork(fetchStatisticsSaga, action.payload.appClient);
        }
    }
}

function* watchFetchClientListSaga() {
    yield* takeEvery(store.fetchClientList, fetchClientListSaga);
}

function* watchAccessRightsSaga() {
    yield takeEvery(store.fetchAccessRights, fetchAccessRightsSaga);
}

function* watchFetchUserSettingsSaga() {
    yield* takeEvery(store.fetchUserSettings, fetchUserSettingsSaga);
}

function* watchSetCurrentAppClient() {
    // when current app client is changed, also re-fetch any data related to previous client
    while (true) {
        yield* take(store.setCurrentAppClient);
        const currentAppClient = yield* select(selectors.getCurrentAppClient);

        // block UI while fetching new values
        yield* put(store.startFetchingClientData());

        // queue re-fetches (currently just model config, licenses are gotten through the page component instead of here)
        yield* all([
            call(fetchModelCustomizationsSaga, currentAppClient),
            call(fetchDaemonConfigsSaga, currentAppClient)
        ]);

        // re-fetching is done
        yield* put(store.finishFetchingClientData());
    }
}

function* startWatchesSaga() {
    yield* all([
        watchSaveModelCustomizationSaga(),
        watchResetAllModelCustomizationsSaga(),
        watchResetSingleModelCustomizationSaga(),
        watchFetchTextEditorFilesSaga(),
        watchFetchTextEditorFileContentsSaga(),
        watchSaveTextEditorFileContentsSaga(),
        watchFetchLicenseStatusSaga(),
        watchUpdateLicenseSaga(),
        watchFetchDaemonConfigsSaga(),
        watchSaveDaemonConfigSaga(),
        watchResetDaemonConfigSaga(),
        watchRemoveDaemonConfigSaga(),
        watchFetchLabelingSaga(),
        watchFetchStatisticsSaga(),
        watchFetchClientListSaga(),
        watchFetchUserSettingsSaga(),
        watchAccessRightsSaga(),
        watchSetCurrentAppClient(),
        watchExportModelCustomizationSaga(),
        watchImportModelCustomizationSaga(),
        watchUserLogOutSaga(),
    ]);
}

/** Initialize app and launch all watch sagas. Dispatch is needed to initialize certain backend client configurations. */
export default function* rootSaga(dispatch: Dispatch) {
    // wait for permission to start initializing app (and logging in the user)
    yield* take(store.startAppInitialization);

    yield* fork(startWatchesSaga);
    yield* call(initializeAppSaga, dispatch);
}
